import { inject, Injectable } from '@angular/core';
import { DialogResult, DialogService } from '@progress/kendo-angular-dialog';
import { BehaviorSubject, filter, lastValueFrom, Subject, take } from 'rxjs';
import { NotificationRef, NotificationService, NotificationSettings } from '@progress/kendo-angular-notification';
import { ToastPage } from '@components/standalone/toast/toast.toast';
import { IdlePermissionDialog } from '@components/dialogs/idle-permission/idle-permission.dialog';
export type AlertOptions = {
    title?: string;
    message: string;
    okButtonText?: string;
};

export type PromptOptions = {
    title?: string;
    message?: string;
    okButtonText?: string;
    placeholderText?: string;
    cancelButtonText?: string;
}

export type ConfirmOptions = {
    title?: string;
    message: string;
    okButtonText?: string;
    cancelButtonText?: string;
    neutralButtonText?: string;
};

@Injectable({
    providedIn: 'root',
})
export class AlertService {
    readonly #dialogSvc = inject(DialogService);
    readonly #notificationSvc = inject(NotificationService);

    promptSubject = new Subject<{ options?: PromptOptions, action: 'prompt' | 'result', result?: string }>();

    constructor() { }

    async alert(options: AlertOptions): Promise<void> {
        if (!options.title) {
            options.title = 'Alert';
        }
        if (!options.okButtonText) {
            options.okButtonText = 'Ok';
        }
        const dialogRef = this.#dialogSvc.open({
            content: options.message,
            title: options.title,
            minWidth: '300px',
            actions: [{ text: options.okButtonText, themeColor: 'primary' }],
        });

        await lastValueFrom(dialogRef.result);
    }

    async confirm(options: ConfirmOptions): Promise<boolean | null> {
        if (!options.title) {
            options.title = 'Confirm';
        }
        if (!options.okButtonText) {
            options.okButtonText = 'Ok';
        }
        if (!options.cancelButtonText) {
            options.cancelButtonText = 'Cancel';
        }

        const buttons: {
            text: string;
            themeColor?: string;
        }[] = [
                {
                    text: options.okButtonText,
                    themeColor: 'primary',
                },
            ];

        if (options.cancelButtonText) {
            buttons.push({
                text: options.cancelButtonText,
            });
        }
        if (options.neutralButtonText) {
            buttons.push({
                text: options.neutralButtonText,
            });
        }

        const dialogRef = this.#dialogSvc.open({
            content: options.message,
            title: options.title,
            minWidth: '300px',
            actions: buttons,
        });

        const result = (await lastValueFrom(dialogRef.result)) as any;
        if (result.text === options.okButtonText) {
            return true;
        } else if (result.text === options.cancelButtonText) {
            return false;
        } else if (result.text === options.neutralButtonText) {
            return null;
        } else {
            return null;
        }
    }

    toast(options: NotificationSettings) {
        const message = options.content as string;
        const _options: NotificationSettings = { position: { horizontal: 'right', vertical: 'top' }, closable: false, hideAfter: 5000, ...options };
        _options.content = ToastPage;
        const notificationRef: NotificationRef = this.#notificationSvc.show(_options);
        if (notificationRef?.content) {
            const comp = notificationRef.content.instance as ToastPage;
            comp.message.set(message);
        }
    }

    toastPersist(options: NotificationSettings) {
        const message = options.content as string;
        const _options: NotificationSettings = { position: { horizontal: 'right', vertical: 'top' }, closable: true, ...options };
        _options.content = ToastPage;
        const notificationRef: NotificationRef = this.#notificationSvc.show(_options);
        if (notificationRef?.content) {
            const comp = notificationRef.content.instance as ToastPage;
            comp.message.set(message);
        }
    }

    async showIdlePermissionDialog() {
        const dialogRef = this.#dialogSvc.open({
            content: IdlePermissionDialog,
            minWidth: '300px',
        });
        await lastValueFrom<DialogResult>(dialogRef.result);
        const dialogInstance = dialogRef.content.instance as IdlePermissionDialog;
        return dialogInstance.permissionGranted;
    }


    async showPrompt(options: PromptOptions) : Promise<string | null> {
       

        this.promptSubject.next(
            {
                options,
                action: 'prompt'
            });

            const result  = await lastValueFrom(this.promptSubject.asObservable().pipe(
                filter((promptOptions) => promptOptions.action === 'result'),
                take(1)));
            if(result.result) {
                return result.result;
            }
            return null;
    }


}
